import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _api_base_domain_: [
      {
        srcPath: '/draft-post-service-api-proxy',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
      {
        srcPath: '/blog/v3/bulk/draft-posts/create',
        destPath: '/v3/bulk/draft-posts/create',
      },
      {
        srcPath: '/blog/v3/bulk/draft-posts',
        destPath: '/v3/bulk/draft-posts',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    _: [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
      {
        srcPath: '/blog/v3/bulk/draft-posts/create',
        destPath: '/v3/bulk/draft-posts/create',
      },
      {
        srcPath: '/blog/v3/bulk/draft-posts',
        destPath: '/v3/bulk/draft-posts',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
      {
        srcPath: '/_api/draft-post-service-api-proxy/v3/bulk/draft-posts',
        destPath: '/v3/bulk/draft-posts',
      },
      {
        srcPath: '/_api/communities-blog-node-api/v3/multilingual',
        destPath: '/v3/multilingual',
      },
      {
        srcPath: '/_api/communities-blog-node-api/v3/stats',
        destPath: '/v3/stats',
      },
      {
        srcPath: '/_api/communities-blog-node-api/v3/moderation',
        destPath: '/v3/moderation',
      },
      {
        srcPath: '/_api/communities-blog-node-api/v3/bulk/moderation',
        destPath: '/v3/bulk/moderation',
      },
    ],
    'social-blog._base_domain_': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/blog/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
      {
        srcPath: '/blog/v3/bulk/draft-posts/create',
        destPath: '/v3/bulk/draft-posts/create',
      },
      {
        srcPath: '/blog/v3/draft-posts/',
        destPath: '/v3/draft-posts',
      },
      {
        srcPath: '/blog/v3/bulk/draft-posts',
        destPath: '/v3/bulk/draft-posts',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/communities-blog-node-api/v3/draft-posts',
        destPath: '/v3/draft-posts',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_blog_draft-posts';

/**
 * Creates a draft post.
 *
 * For 3rd-party apps, `memberId` is a required field.
 *
 * In `categoryIds`, only categories that already exist will be added to the draft post. Including
 * `categoryIds` that do not exist will not return an error, but they will not be added to the `categoryIds` array.
 * If some of the categories exist, those will be added while those that do not exist will not be added to `categoryIds`.
 * You can check which categories are available with
 * [List Categories](https://dev.wix.com/docs/rest/business-solutions/blog/category/list-categories).
 */
export function createDraftPost(payload: object): RequestOptionsFactory<any> {
  function __createDraftPost({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'draftPost.editedDate' },
          { path: 'draftPost.scheduledPublishDate' },
          { path: 'draftPost.firstPublishedDate' },
          { path: 'draftPost.createdDate' },
          { path: 'draftPost.heroImage.urlExpirationDate' },
          { path: 'draftPost.richContent.metadata.createdTimestamp' },
          { path: 'draftPost.richContent.metadata.updatedTimestamp' },
          { path: 'draftPost.moderationDetails.submittedDate' },
          { path: 'draftPost.moderationDetails.moderationDate' },
          { path: 'draftPost.media.wixMedia.image.urlExpirationDate' },
          { path: 'draftPost.media.wixMedia.videoV2.urlExpirationDate' },
          {
            path: 'draftPost.media.wixMedia.videoV2.resolutions.urlExpirationDate',
          },
          {
            path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'draftPost.media.wixMedia.videoV2.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'draftPost.heroImage.focalPoint.x' },
          { path: 'draftPost.heroImage.focalPoint.y' },
          { path: 'draftPost.media.wixMedia.image.focalPoint.x' },
          { path: 'draftPost.media.wixMedia.image.focalPoint.y' },
          {
            path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
          },
          {
            path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
          },
          { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.x' },
          { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.y' },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'draftPost.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'draftPost.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'draftPost.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'draftPost.richContent.nodes.galleryData.options.item.ratio',
          },
          { path: 'draftPost.richContent.nodes.imageData.image.duration' },
          { path: 'draftPost.richContent.nodes.mapData.mapSettings.lat' },
          { path: 'draftPost.richContent.nodes.mapData.mapSettings.lng' },
          { path: 'draftPost.richContent.nodes.pollData.poll.image.duration' },
          {
            path: 'draftPost.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'draftPost.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'draftPost.richContent.nodes.appEmbedData.image.duration' },
          { path: 'draftPost.richContent.nodes.videoData.video.duration' },
          { path: 'draftPost.richContent.nodes.videoData.thumbnail.duration' },
          { path: 'draftPost.richContent.nodes.audioData.audio.duration' },
          { path: 'draftPost.richContent.nodes.audioData.coverImage.duration' },
          {
            path: 'draftPost.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'draftPost.richContent.nodes.shapeData.shape.duration' },
          {
            path: 'draftPost.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'draftPost.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'draftPost.richContent.nodes.tocData.fontSize' },
          { path: 'draftPost.richContent.nodes.tocData.itemSpacing' },
          {
            path: 'draftPost.richContent.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.CreateDraftPost',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/draft-posts', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftPost.editedDate' },
              { path: 'draftPost.scheduledPublishDate' },
              { path: 'draftPost.firstPublishedDate' },
              { path: 'draftPost.createdDate' },
              { path: 'draftPost.heroImage.urlExpirationDate' },
              { path: 'draftPost.richContent.metadata.createdTimestamp' },
              { path: 'draftPost.richContent.metadata.updatedTimestamp' },
              { path: 'draftPost.moderationDetails.submittedDate' },
              { path: 'draftPost.moderationDetails.moderationDate' },
              { path: 'draftPost.media.wixMedia.image.urlExpirationDate' },
              { path: 'draftPost.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftPost.heroImage.focalPoint.x' },
              { path: 'draftPost.heroImage.focalPoint.y' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.y' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.y' },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.options.item.ratio',
              },
              { path: 'draftPost.richContent.nodes.imageData.image.duration' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lat' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lng' },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.appEmbedData.image.duration',
              },
              { path: 'draftPost.richContent.nodes.videoData.video.duration' },
              {
                path: 'draftPost.richContent.nodes.videoData.thumbnail.duration',
              },
              { path: 'draftPost.richContent.nodes.audioData.audio.duration' },
              {
                path: 'draftPost.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'draftPost.richContent.nodes.shapeData.shape.duration' },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'draftPost.richContent.nodes.tocData.fontSize' },
              { path: 'draftPost.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'draftPost.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createDraftPost;
}

/** Creates multiple draft posts. */
export function bulkCreateDraftPosts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateDraftPosts({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'draftPosts.editedDate' },
          { path: 'draftPosts.scheduledPublishDate' },
          { path: 'draftPosts.firstPublishedDate' },
          { path: 'draftPosts.createdDate' },
          { path: 'draftPosts.heroImage.urlExpirationDate' },
          { path: 'draftPosts.richContent.metadata.createdTimestamp' },
          { path: 'draftPosts.richContent.metadata.updatedTimestamp' },
          { path: 'draftPosts.moderationDetails.submittedDate' },
          { path: 'draftPosts.moderationDetails.moderationDate' },
          { path: 'draftPosts.media.wixMedia.image.urlExpirationDate' },
          { path: 'draftPosts.media.wixMedia.videoV2.urlExpirationDate' },
          {
            path: 'draftPosts.media.wixMedia.videoV2.resolutions.urlExpirationDate',
          },
          {
            path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'draftPosts.media.wixMedia.videoV2.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'draftPosts.heroImage.focalPoint.x' },
          { path: 'draftPosts.heroImage.focalPoint.y' },
          { path: 'draftPosts.media.wixMedia.image.focalPoint.x' },
          { path: 'draftPosts.media.wixMedia.image.focalPoint.y' },
          {
            path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
          },
          {
            path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
          },
          { path: 'draftPosts.media.wixMedia.videoV2.posters.focalPoint.x' },
          { path: 'draftPosts.media.wixMedia.videoV2.posters.focalPoint.y' },
          {
            path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'draftPosts.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'draftPosts.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'draftPosts.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'draftPosts.richContent.nodes.galleryData.options.item.ratio',
          },
          { path: 'draftPosts.richContent.nodes.imageData.image.duration' },
          { path: 'draftPosts.richContent.nodes.mapData.mapSettings.lat' },
          { path: 'draftPosts.richContent.nodes.mapData.mapSettings.lng' },
          { path: 'draftPosts.richContent.nodes.pollData.poll.image.duration' },
          {
            path: 'draftPosts.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'draftPosts.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'draftPosts.richContent.nodes.appEmbedData.image.duration' },
          { path: 'draftPosts.richContent.nodes.videoData.video.duration' },
          { path: 'draftPosts.richContent.nodes.videoData.thumbnail.duration' },
          { path: 'draftPosts.richContent.nodes.audioData.audio.duration' },
          {
            path: 'draftPosts.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'draftPosts.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'draftPosts.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'draftPosts.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'draftPosts.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'draftPosts.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'draftPosts.richContent.nodes.shapeData.shape.duration' },
          {
            path: 'draftPosts.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'draftPosts.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'draftPosts.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'draftPosts.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'draftPosts.richContent.nodes.tocData.fontSize' },
          { path: 'draftPosts.richContent.nodes.tocData.itemSpacing' },
          {
            path: 'draftPosts.richContent.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.BulkCreateDraftPosts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/bulk/draft-posts/create', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.editedDate' },
              { path: 'results.item.scheduledPublishDate' },
              { path: 'results.item.firstPublishedDate' },
              { path: 'results.item.createdDate' },
              { path: 'results.item.heroImage.urlExpirationDate' },
              { path: 'results.item.richContent.metadata.createdTimestamp' },
              { path: 'results.item.richContent.metadata.updatedTimestamp' },
              { path: 'results.item.moderationDetails.submittedDate' },
              { path: 'results.item.moderationDetails.moderationDate' },
              { path: 'results.item.media.wixMedia.image.urlExpirationDate' },
              { path: 'results.item.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.heroImage.focalPoint.x' },
              { path: 'results.item.heroImage.focalPoint.y' },
              { path: 'results.item.media.wixMedia.image.focalPoint.x' },
              { path: 'results.item.media.wixMedia.image.focalPoint.y' },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.focalPoint.x',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.focalPoint.y',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'results.item.richContent.nodes.tocData.fontSize' },
              { path: 'results.item.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'results.item.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateDraftPosts;
}

/** Updates multiple draft posts. */
export function bulkUpdateDraftPosts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateDraftPosts({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'scheduledPublishDate' },
          { path: 'draftPosts.draftPost.editedDate' },
          { path: 'draftPosts.draftPost.scheduledPublishDate' },
          { path: 'draftPosts.draftPost.firstPublishedDate' },
          { path: 'draftPosts.draftPost.createdDate' },
          { path: 'draftPosts.draftPost.heroImage.urlExpirationDate' },
          {
            path: 'draftPosts.draftPost.richContent.metadata.createdTimestamp',
          },
          {
            path: 'draftPosts.draftPost.richContent.metadata.updatedTimestamp',
          },
          { path: 'draftPosts.draftPost.moderationDetails.submittedDate' },
          { path: 'draftPosts.draftPost.moderationDetails.moderationDate' },
          {
            path: 'draftPosts.draftPost.media.wixMedia.image.urlExpirationDate',
          },
          {
            path: 'draftPosts.draftPost.media.wixMedia.videoV2.urlExpirationDate',
          },
          {
            path: 'draftPosts.draftPost.media.wixMedia.videoV2.resolutions.urlExpirationDate',
          },
          {
            path: 'draftPosts.draftPost.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'draftPosts.draftPost.media.wixMedia.videoV2.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'draftPosts.fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'draftPosts.draftPost.heroImage.focalPoint.x' },
          { path: 'draftPosts.draftPost.heroImage.focalPoint.y' },
          { path: 'draftPosts.draftPost.media.wixMedia.image.focalPoint.x' },
          { path: 'draftPosts.draftPost.media.wixMedia.image.focalPoint.y' },
          {
            path: 'draftPosts.draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
          },
          {
            path: 'draftPosts.draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
          },
          {
            path: 'draftPosts.draftPost.media.wixMedia.videoV2.posters.focalPoint.x',
          },
          {
            path: 'draftPosts.draftPost.media.wixMedia.videoV2.posters.focalPoint.y',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.imageData.image.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.pollData.poll.image.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.appEmbedData.image.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.videoData.video.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.audioData.audio.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.audioData.coverImage.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.shapeData.shape.duration',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'draftPosts.draftPost.richContent.nodes.tocData.fontSize' },
          {
            path: 'draftPosts.draftPost.richContent.nodes.tocData.itemSpacing',
          },
          {
            path: 'draftPosts.draftPost.richContent.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.BulkUpdateDraftPosts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/draft-posts/update', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.editedDate' },
              { path: 'results.item.scheduledPublishDate' },
              { path: 'results.item.firstPublishedDate' },
              { path: 'results.item.createdDate' },
              { path: 'results.item.heroImage.urlExpirationDate' },
              { path: 'results.item.richContent.metadata.createdTimestamp' },
              { path: 'results.item.richContent.metadata.updatedTimestamp' },
              { path: 'results.item.moderationDetails.submittedDate' },
              { path: 'results.item.moderationDetails.moderationDate' },
              { path: 'results.item.media.wixMedia.image.urlExpirationDate' },
              { path: 'results.item.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.heroImage.focalPoint.x' },
              { path: 'results.item.heroImage.focalPoint.y' },
              { path: 'results.item.media.wixMedia.image.focalPoint.x' },
              { path: 'results.item.media.wixMedia.image.focalPoint.y' },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.focalPoint.x',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.focalPoint.y',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'results.item.richContent.nodes.tocData.fontSize' },
              { path: 'results.item.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'results.item.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateDraftPosts;
}

/**
 * Retrieves a list of up to 100 deleted draft posts.
 *
 * List Draft Posts runs with these defaults, which you can override:
 * - `editedDate` is sorted in descending order. In this case,`editedDate` implies the date the post was deleted.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 */
export function listDeletedDraftPosts(
  payload: object
): RequestOptionsFactory<any> {
  function __listDeletedDraftPosts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.ListDeletedDraftPosts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/draft-posts/trash-bin', data: payload, host }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftPosts.editedDate' },
              { path: 'draftPosts.scheduledPublishDate' },
              { path: 'draftPosts.firstPublishedDate' },
              { path: 'draftPosts.createdDate' },
              { path: 'draftPosts.heroImage.urlExpirationDate' },
              { path: 'draftPosts.richContent.metadata.createdTimestamp' },
              { path: 'draftPosts.richContent.metadata.updatedTimestamp' },
              { path: 'draftPosts.moderationDetails.submittedDate' },
              { path: 'draftPosts.moderationDetails.moderationDate' },
              { path: 'draftPosts.media.wixMedia.image.urlExpirationDate' },
              { path: 'draftPosts.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftPosts.heroImage.focalPoint.x' },
              { path: 'draftPosts.heroImage.focalPoint.y' },
              { path: 'draftPosts.media.wixMedia.image.focalPoint.x' },
              { path: 'draftPosts.media.wixMedia.image.focalPoint.y' },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.focalPoint.x',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.focalPoint.y',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.options.item.ratio',
              },
              { path: 'draftPosts.richContent.nodes.imageData.image.duration' },
              { path: 'draftPosts.richContent.nodes.mapData.mapSettings.lat' },
              { path: 'draftPosts.richContent.nodes.mapData.mapSettings.lng' },
              {
                path: 'draftPosts.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.appEmbedData.image.duration',
              },
              { path: 'draftPosts.richContent.nodes.videoData.video.duration' },
              {
                path: 'draftPosts.richContent.nodes.videoData.thumbnail.duration',
              },
              { path: 'draftPosts.richContent.nodes.audioData.audio.duration' },
              {
                path: 'draftPosts.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'draftPosts.richContent.nodes.shapeData.shape.duration' },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'draftPosts.richContent.nodes.tocData.fontSize' },
              { path: 'draftPosts.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'draftPosts.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listDeletedDraftPosts;
}

/** Retrieves a draft post by the provided ID. */
export function getDraftPost(payload: object): RequestOptionsFactory<any> {
  function __getDraftPost({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.GetDraftPost',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/draft-posts/{draftPostId}', data: payload, host }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftPost.editedDate' },
              { path: 'draftPost.scheduledPublishDate' },
              { path: 'draftPost.firstPublishedDate' },
              { path: 'draftPost.createdDate' },
              { path: 'draftPost.heroImage.urlExpirationDate' },
              { path: 'draftPost.richContent.metadata.createdTimestamp' },
              { path: 'draftPost.richContent.metadata.updatedTimestamp' },
              { path: 'draftPost.moderationDetails.submittedDate' },
              { path: 'draftPost.moderationDetails.moderationDate' },
              { path: 'draftPost.media.wixMedia.image.urlExpirationDate' },
              { path: 'draftPost.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftPost.heroImage.focalPoint.x' },
              { path: 'draftPost.heroImage.focalPoint.y' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.y' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.y' },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.options.item.ratio',
              },
              { path: 'draftPost.richContent.nodes.imageData.image.duration' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lat' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lng' },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.appEmbedData.image.duration',
              },
              { path: 'draftPost.richContent.nodes.videoData.video.duration' },
              {
                path: 'draftPost.richContent.nodes.videoData.thumbnail.duration',
              },
              { path: 'draftPost.richContent.nodes.audioData.audio.duration' },
              {
                path: 'draftPost.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'draftPost.richContent.nodes.shapeData.shape.duration' },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'draftPost.richContent.nodes.tocData.fontSize' },
              { path: 'draftPost.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'draftPost.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDraftPost;
}

/** Updates a draft post. */
export function updateDraftPost(payload: object): RequestOptionsFactory<any> {
  function __updateDraftPost({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'scheduledPublishDate' },
          { path: 'draftPost.editedDate' },
          { path: 'draftPost.scheduledPublishDate' },
          { path: 'draftPost.firstPublishedDate' },
          { path: 'draftPost.createdDate' },
          { path: 'draftPost.heroImage.urlExpirationDate' },
          { path: 'draftPost.richContent.metadata.createdTimestamp' },
          { path: 'draftPost.richContent.metadata.updatedTimestamp' },
          { path: 'draftPost.moderationDetails.submittedDate' },
          { path: 'draftPost.moderationDetails.moderationDate' },
          { path: 'draftPost.media.wixMedia.image.urlExpirationDate' },
          { path: 'draftPost.media.wixMedia.videoV2.urlExpirationDate' },
          {
            path: 'draftPost.media.wixMedia.videoV2.resolutions.urlExpirationDate',
          },
          {
            path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'draftPost.media.wixMedia.videoV2.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'draftPost.heroImage.focalPoint.x' },
          { path: 'draftPost.heroImage.focalPoint.y' },
          { path: 'draftPost.media.wixMedia.image.focalPoint.x' },
          { path: 'draftPost.media.wixMedia.image.focalPoint.y' },
          {
            path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
          },
          {
            path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
          },
          { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.x' },
          { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.y' },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'draftPost.richContent.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'draftPost.richContent.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'draftPost.richContent.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'draftPost.richContent.nodes.galleryData.options.item.ratio',
          },
          { path: 'draftPost.richContent.nodes.imageData.image.duration' },
          { path: 'draftPost.richContent.nodes.mapData.mapSettings.lat' },
          { path: 'draftPost.richContent.nodes.mapData.mapSettings.lng' },
          { path: 'draftPost.richContent.nodes.pollData.poll.image.duration' },
          {
            path: 'draftPost.richContent.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'draftPost.richContent.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'draftPost.richContent.nodes.appEmbedData.image.duration' },
          { path: 'draftPost.richContent.nodes.videoData.video.duration' },
          { path: 'draftPost.richContent.nodes.videoData.thumbnail.duration' },
          { path: 'draftPost.richContent.nodes.audioData.audio.duration' },
          { path: 'draftPost.richContent.nodes.audioData.coverImage.duration' },
          {
            path: 'draftPost.richContent.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'draftPost.richContent.nodes.shapeData.shape.duration' },
          {
            path: 'draftPost.richContent.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'draftPost.richContent.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'draftPost.richContent.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'draftPost.richContent.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'draftPost.richContent.nodes.tocData.fontSize' },
          { path: 'draftPost.richContent.nodes.tocData.itemSpacing' },
          {
            path: 'draftPost.richContent.nodes.smartBlockCellData.shape.duration',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.UpdateDraftPost',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        {
          protoPath: '/v3/draft-posts/{draftPost.id}',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftPost.editedDate' },
              { path: 'draftPost.scheduledPublishDate' },
              { path: 'draftPost.firstPublishedDate' },
              { path: 'draftPost.createdDate' },
              { path: 'draftPost.heroImage.urlExpirationDate' },
              { path: 'draftPost.richContent.metadata.createdTimestamp' },
              { path: 'draftPost.richContent.metadata.updatedTimestamp' },
              { path: 'draftPost.moderationDetails.submittedDate' },
              { path: 'draftPost.moderationDetails.moderationDate' },
              { path: 'draftPost.media.wixMedia.image.urlExpirationDate' },
              { path: 'draftPost.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftPost.heroImage.focalPoint.x' },
              { path: 'draftPost.heroImage.focalPoint.y' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.y' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.y' },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.options.item.ratio',
              },
              { path: 'draftPost.richContent.nodes.imageData.image.duration' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lat' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lng' },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.appEmbedData.image.duration',
              },
              { path: 'draftPost.richContent.nodes.videoData.video.duration' },
              {
                path: 'draftPost.richContent.nodes.videoData.thumbnail.duration',
              },
              { path: 'draftPost.richContent.nodes.audioData.audio.duration' },
              {
                path: 'draftPost.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'draftPost.richContent.nodes.shapeData.shape.duration' },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'draftPost.richContent.nodes.tocData.fontSize' },
              { path: 'draftPost.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'draftPost.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateDraftPost;
}

/**
 * Moves a draft post to the trash bin.
 * A published post can also be deleted by the `post.id`. See the [Posts API](https://dev.wix.com/docs/rest/business-solutions/blog/posts-stats/post-object) for more information about posts.
 *
 * To permanently delete a post bypassing the trash bin, set the `permanent` field value to `true`. The post can't be restored after this.
 */
export function deleteDraftPost(payload: object): RequestOptionsFactory<any> {
  function __deleteDraftPost({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.DeleteDraftPost',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/draft-posts/{draftPostId}', data: payload, host }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteDraftPost;
}

/**
 * Permanently deletes a draft post that is currently in the trash bin.
 *
 * This action is permanent and can't be reversed.
 */
export function removeFromTrashBin(
  payload: object
): RequestOptionsFactory<any> {
  function __removeFromTrashBin({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.RemoveFromTrashBin',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        {
          protoPath: '/v3/draft-posts/trash-bin/{draftPostId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __removeFromTrashBin;
}

/** Deletes multiple draft posts. */
export function bulkDeleteDraftPosts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteDraftPosts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.BulkDeleteDraftPosts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/bulk/draft-posts', data: payload, host }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.editedDate' },
              { path: 'results.item.scheduledPublishDate' },
              { path: 'results.item.firstPublishedDate' },
              { path: 'results.item.createdDate' },
              { path: 'results.item.heroImage.urlExpirationDate' },
              { path: 'results.item.richContent.metadata.createdTimestamp' },
              { path: 'results.item.richContent.metadata.updatedTimestamp' },
              { path: 'results.item.moderationDetails.submittedDate' },
              { path: 'results.item.moderationDetails.moderationDate' },
              { path: 'results.item.media.wixMedia.image.urlExpirationDate' },
              { path: 'results.item.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.heroImage.focalPoint.x' },
              { path: 'results.item.heroImage.focalPoint.y' },
              { path: 'results.item.media.wixMedia.image.focalPoint.x' },
              { path: 'results.item.media.wixMedia.image.focalPoint.y' },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.focalPoint.x',
              },
              {
                path: 'results.item.media.wixMedia.videoV2.posters.focalPoint.y',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.richContent.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.richContent.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.richContent.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.richContent.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.richContent.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.richContent.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'results.item.richContent.nodes.tocData.fontSize' },
              { path: 'results.item.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'results.item.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkDeleteDraftPosts;
}

/**
 * Retrieves a list of up to 100 draft posts per request.
 *
 * List Draft Posts runs with these defaults, which you can override:
 * - `editedDate` is sorted in descending order.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 */
export function listDraftPosts(payload: object): RequestOptionsFactory<any> {
  function __listDraftPosts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.ListDraftPosts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/draft-posts', data: payload, host }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftPosts.editedDate' },
              { path: 'draftPosts.scheduledPublishDate' },
              { path: 'draftPosts.firstPublishedDate' },
              { path: 'draftPosts.createdDate' },
              { path: 'draftPosts.heroImage.urlExpirationDate' },
              { path: 'draftPosts.richContent.metadata.createdTimestamp' },
              { path: 'draftPosts.richContent.metadata.updatedTimestamp' },
              { path: 'draftPosts.moderationDetails.submittedDate' },
              { path: 'draftPosts.moderationDetails.moderationDate' },
              { path: 'draftPosts.media.wixMedia.image.urlExpirationDate' },
              { path: 'draftPosts.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftPosts.heroImage.focalPoint.x' },
              { path: 'draftPosts.heroImage.focalPoint.y' },
              { path: 'draftPosts.media.wixMedia.image.focalPoint.x' },
              { path: 'draftPosts.media.wixMedia.image.focalPoint.y' },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.focalPoint.x',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.focalPoint.y',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.options.item.ratio',
              },
              { path: 'draftPosts.richContent.nodes.imageData.image.duration' },
              { path: 'draftPosts.richContent.nodes.mapData.mapSettings.lat' },
              { path: 'draftPosts.richContent.nodes.mapData.mapSettings.lng' },
              {
                path: 'draftPosts.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.appEmbedData.image.duration',
              },
              { path: 'draftPosts.richContent.nodes.videoData.video.duration' },
              {
                path: 'draftPosts.richContent.nodes.videoData.thumbnail.duration',
              },
              { path: 'draftPosts.richContent.nodes.audioData.audio.duration' },
              {
                path: 'draftPosts.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'draftPosts.richContent.nodes.shapeData.shape.duration' },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'draftPosts.richContent.nodes.tocData.fontSize' },
              { path: 'draftPosts.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'draftPosts.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listDraftPosts;
}

/** Retrieves a deleted draft post from the trash bin by the provided ID. */
export function getDeletedDraftPost(
  payload: object
): RequestOptionsFactory<any> {
  function __getDeletedDraftPost({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.GetDeletedDraftPost',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        {
          protoPath: '/v3/draft-posts/trash-bin/{draftPostId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftPost.editedDate' },
              { path: 'draftPost.scheduledPublishDate' },
              { path: 'draftPost.firstPublishedDate' },
              { path: 'draftPost.createdDate' },
              { path: 'draftPost.heroImage.urlExpirationDate' },
              { path: 'draftPost.richContent.metadata.createdTimestamp' },
              { path: 'draftPost.richContent.metadata.updatedTimestamp' },
              { path: 'draftPost.moderationDetails.submittedDate' },
              { path: 'draftPost.moderationDetails.moderationDate' },
              { path: 'draftPost.media.wixMedia.image.urlExpirationDate' },
              { path: 'draftPost.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftPost.heroImage.focalPoint.x' },
              { path: 'draftPost.heroImage.focalPoint.y' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.y' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.y' },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.options.item.ratio',
              },
              { path: 'draftPost.richContent.nodes.imageData.image.duration' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lat' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lng' },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.appEmbedData.image.duration',
              },
              { path: 'draftPost.richContent.nodes.videoData.video.duration' },
              {
                path: 'draftPost.richContent.nodes.videoData.thumbnail.duration',
              },
              { path: 'draftPost.richContent.nodes.audioData.audio.duration' },
              {
                path: 'draftPost.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'draftPost.richContent.nodes.shapeData.shape.duration' },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'draftPost.richContent.nodes.tocData.fontSize' },
              { path: 'draftPost.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'draftPost.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDeletedDraftPost;
}

/** Restores a deleted draft post from the trash bin by the provided ID. */
export function restoreFromTrashBin(
  payload: object
): RequestOptionsFactory<any> {
  function __restoreFromTrashBin({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.RestoreFromTrashBin',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        {
          protoPath: '/v3/draft-posts/trash-bin/{draftPostId}/restore',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftPost.editedDate' },
              { path: 'draftPost.scheduledPublishDate' },
              { path: 'draftPost.firstPublishedDate' },
              { path: 'draftPost.createdDate' },
              { path: 'draftPost.heroImage.urlExpirationDate' },
              { path: 'draftPost.richContent.metadata.createdTimestamp' },
              { path: 'draftPost.richContent.metadata.updatedTimestamp' },
              { path: 'draftPost.moderationDetails.submittedDate' },
              { path: 'draftPost.moderationDetails.moderationDate' },
              { path: 'draftPost.media.wixMedia.image.urlExpirationDate' },
              { path: 'draftPost.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftPost.heroImage.focalPoint.x' },
              { path: 'draftPost.heroImage.focalPoint.y' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.image.focalPoint.y' },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'draftPost.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.x' },
              { path: 'draftPost.media.wixMedia.videoV2.posters.focalPoint.y' },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'draftPost.richContent.nodes.galleryData.options.item.ratio',
              },
              { path: 'draftPost.richContent.nodes.imageData.image.duration' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lat' },
              { path: 'draftPost.richContent.nodes.mapData.mapSettings.lng' },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'draftPost.richContent.nodes.appEmbedData.image.duration',
              },
              { path: 'draftPost.richContent.nodes.videoData.video.duration' },
              {
                path: 'draftPost.richContent.nodes.videoData.thumbnail.duration',
              },
              { path: 'draftPost.richContent.nodes.audioData.audio.duration' },
              {
                path: 'draftPost.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'draftPost.richContent.nodes.shapeData.shape.duration' },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'draftPost.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'draftPost.richContent.nodes.tocData.fontSize' },
              { path: 'draftPost.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'draftPost.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __restoreFromTrashBin;
}

/**
 * Creates a query to retrieve a list of up to 100 draft posts.
 *
 *
 * The `queryDraftPosts()` function builds a query to retrieve a list of up to 100 draft posts, and returns a `DraftPostsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `DraftPostsQueryBuilder` functions onto the query. `DraftPostsQueryBuilder` functions enable you to sort, filter, and control the results that `queryDraftPosts()` returns.
 *
 * `queryDraftPosts()` runs with these `DraftPostsQueryBuilder` defaults that can be overridden:
 * - `limit(50)`
 * - `ascending('_id')`
 *
 * The following `DraftPostsQueryBuilder` functions are supported for `queryDraftPosts()`. For a full description of the Draft Posts object, see the object returned for the `items` property in `DraftPostsQueryResult`.
 */
export function queryDraftPosts(payload: object): RequestOptionsFactory<any> {
  function __queryDraftPosts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.QueryDraftPosts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        { protoPath: '/v3/draft-posts/query', data: payload, host }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftPosts.editedDate' },
              { path: 'draftPosts.scheduledPublishDate' },
              { path: 'draftPosts.firstPublishedDate' },
              { path: 'draftPosts.createdDate' },
              { path: 'draftPosts.heroImage.urlExpirationDate' },
              { path: 'draftPosts.richContent.metadata.createdTimestamp' },
              { path: 'draftPosts.richContent.metadata.updatedTimestamp' },
              { path: 'draftPosts.moderationDetails.submittedDate' },
              { path: 'draftPosts.moderationDetails.moderationDate' },
              { path: 'draftPosts.media.wixMedia.image.urlExpirationDate' },
              { path: 'draftPosts.media.wixMedia.videoV2.urlExpirationDate' },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.urlExpirationDate',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftPosts.heroImage.focalPoint.x' },
              { path: 'draftPosts.heroImage.focalPoint.y' },
              { path: 'draftPosts.media.wixMedia.image.focalPoint.x' },
              { path: 'draftPosts.media.wixMedia.image.focalPoint.y' },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.focalPoint.x',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.resolutions.poster.focalPoint.y',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.focalPoint.x',
              },
              {
                path: 'draftPosts.media.wixMedia.videoV2.posters.focalPoint.y',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.galleryData.options.item.ratio',
              },
              { path: 'draftPosts.richContent.nodes.imageData.image.duration' },
              { path: 'draftPosts.richContent.nodes.mapData.mapSettings.lat' },
              { path: 'draftPosts.richContent.nodes.mapData.mapSettings.lng' },
              {
                path: 'draftPosts.richContent.nodes.pollData.poll.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.appEmbedData.image.duration',
              },
              { path: 'draftPosts.richContent.nodes.videoData.video.duration' },
              {
                path: 'draftPosts.richContent.nodes.videoData.thumbnail.duration',
              },
              { path: 'draftPosts.richContent.nodes.audioData.audio.duration' },
              {
                path: 'draftPosts.richContent.nodes.audioData.coverImage.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'draftPosts.richContent.nodes.shapeData.shape.duration' },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'draftPosts.richContent.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'draftPosts.richContent.nodes.tocData.fontSize' },
              { path: 'draftPosts.richContent.nodes.tocData.itemSpacing' },
              {
                path: 'draftPosts.richContent.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryDraftPosts;
}

/**
 * Publishes a draft post by ID. This creates a new [post](https://dev.wix.com/docs/rest/business-solutions/blog/posts-stats/post-object)
 * with the data from the draft post.
 *
 * If the draft post was already published, the published post will be updated with the latest values
 * from the draft post.
 */
export function publishDraftPost(payload: object): RequestOptionsFactory<any> {
  function __publishDraftPost({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.blog.v3.draft',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.npm.communities.platformized.blog.v3.DraftPostService.PublishDraftPost',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressNpmCommunitiesPlatformizedBlogV3DraftPostServiceUrl(
        {
          protoPath: '/v3/draft-posts/{draftPostId}/publish',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __publishDraftPost;
}
